import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchTransactions, fetchTransaction, fetchTransactionsByTag, createTransaction, updateTransaction, deleteTransaction } from '@/lib/api'
import type { Transaction } from '@/lib/api'
import { mutationErrorHandler, filterByScope } from '@/lib/utils'
import type { ScopeFilter } from '@/lib/utils'
import { useScope } from '@/lib/scope-context'

export type { Transaction, TransactionItem } from '@/lib/api'

export function useTransactions(month: string, scopeOverride?: ScopeFilter) {
  const { scope } = useScope()
  const effectiveScope = scopeOverride ?? scope

  const { data: allTransactions = [], ...rest } = useQuery<Transaction[]>({
    queryKey: ['transactions', month, 'all'],
    queryFn: () => fetchTransactions(month),
  })

  const data = useMemo(
    () => filterByScope(allTransactions, effectiveScope),
    [allTransactions, effectiveScope],
  )

  return { data, ...rest }
}

export function useTransactionsByTag(tagId: string | undefined, scopeOverride?: ScopeFilter) {
  const { scope } = useScope()
  const effectiveScope = scopeOverride ?? scope

  const { data: allTransactions = [], ...rest } = useQuery<Transaction[]>({
    queryKey: ['transactions', 'tag', tagId, 'all'],
    queryFn: () => fetchTransactionsByTag(tagId!),
    enabled: !!tagId,
  })

  const data = useMemo(
    () => filterByScope(allTransactions, effectiveScope),
    [allTransactions, effectiveScope],
  )

  return { data, ...rest }
}

export function useTransaction(id: string | undefined) {
  const queryClient = useQueryClient()
  return useQuery<Transaction | null>({
    queryKey: ['transaction', id],
    queryFn: () => fetchTransaction(id!),
    enabled: !!id,
    initialData: () => {
      const queries = queryClient.getQueriesData<Transaction[]>({ queryKey: ['transactions'] })
      for (const [, transactions] of queries) {
        const tx = transactions?.find(t => t.id === id)
        if (tx) return tx
      }
      return undefined
    },
  })
}

export function useCreateTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createTransaction,
    onSuccess: () => {
      toast.success('取引を保存しました')
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['budgets', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['tags', 'last-used'] })
    },
    onError: mutationErrorHandler('保存に失敗しました'),
  })
}

export function useUpdateTransaction(month?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateTransaction>[1] }) =>
      updateTransaction(id, data),
    onSuccess: (_data, variables) => {
      toast.success('取引を更新しました')
      if (month) queryClient.invalidateQueries({ queryKey: ['transactions', month] })
      queryClient.invalidateQueries({ queryKey: ['transaction', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['budgets', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['tags', 'last-used'] })
    },
    onError: mutationErrorHandler('更新に失敗しました'),
  })
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteTransaction,
    onSuccess: () => {
      toast.success('取引を削除しました')
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['budgets', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['tags', 'last-used'] })
    },
    onError: mutationErrorHandler('削除に失敗しました'),
  })
}
